import {
	LegalLayout,
	type LegalSection,
} from '#app/components/marketing/legal-layout.tsx'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/refund-policy.ts'

export const meta: Route.MetaFunction = ({ matches }) =>
	buildMeta({
		title: 'Refund Policy - Syrcha Privacy Compliance Scanner',
		description:
			'Learn about our refund policy and when refunds may be available for Syrcha compliance monitoring services.',
		matches,
	})

export default function RefundPolicyRoute() {
	const sections: LegalSection[] = [
		{
			id: 'cancelling',
			title: 'Cancelling',
			body: (
				<>
					<p>
						Cancel from <strong>Settings → Billing → Manage or cancel</strong>.
						It opens Stripe's billing portal, where you can also change your
						payment method and download every invoice. You do not have to email
						us, and nobody will call you about it.
					</p>
					<p>
						Cancelling stops the next charge. Your plan keeps running until the
						end of the period you already paid for, and your scans and reports
						stay available for that whole time.
					</p>
				</>
			),
		},
		{
			id: 'refunds',
			title: 'When we refund',
			body: (
				<>
					<p>We refund in these cases, without argument:</p>
					<ul>
						<li>
							<strong>Duplicate charges</strong>: you were billed more than once
							for the same period. Refunded in full.
						</li>
						<li>
							<strong>Extended outage</strong>: the service was unavailable for
							more than 72 consecutive hours and we could not fix it. Refunded
							for the affected period.
						</li>
						<li>
							<strong>Consumer withdrawal (EU, EEA, UK)</strong>: if you bought
							as a consumer rather than for a business, you have 14 days from
							purchase to withdraw. If you asked us to start the service inside
							that window, we deduct the part you used and refund the rest.
						</li>
						<li>
							<strong>Anywhere your local law requires it</strong>: including
							the Australian Consumer Law guarantees, which we cannot and do not
							exclude.
						</li>
					</ul>
				</>
			),
		},
		{
			id: 'general',
			title: 'When we do not',
			body: (
				<>
					<p>
						Outside those cases, subscription fees are not refundable. In
						particular, we do not refund the unused part of a period after you
						cancel, or a period in which the scanner ran and produced results
						you did not like.
					</p>
					<p>
						That second one is deliberate, and it is worth saying why: a scan
						that reports nothing wrong is a real result, not a failed delivery.
						What we owe you is that the check ran honestly and that the finding
						is reproducible, which is what the signed report is for. If you
						think a finding is wrong, dispute it; that is a product feature, not
						a billing matter.
					</p>
					<p>
						Discounted and promotional subscriptions follow the same rules as
						any other. Free plans and free trials are never charged, so there is
						nothing to refund.
					</p>
				</>
			),
		},
		{
			id: 'request',
			title: 'Asking for one',
			body: (
				<>
					<p>
						Email <a href="mailto:billing@syrcha.com">billing@syrcha.com</a>{' '}
						with your account email and the invoice number. You do not need to
						explain yourself for a duplicate charge or a withdrawal request;
						those we just process.
					</p>
					<p>
						We reply within 5 business days. Approved refunds go back to the
						original payment method through Stripe, which typically takes
						another 5 to 10 days to appear depending on your bank.
					</p>
				</>
			),
		},
	]

	return (
		<LegalLayout
			eyebrow="/ legal · refunds"
			title={
				<>
					Refund <span className="accent-underline">policy</span>.
				</>
			}
			effectiveDate="2025-09-01"
			lastUpdatedDate="2026-08-07"
			intro={
				<p>
					You can cancel from your settings at any time, without emailing
					anyone. This page covers the narrower question of when money comes
					back, and it leads with the cases where it does.
				</p>
			}
			sections={sections}
		/>
	)
}
